import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  LinearProgress,
  IconButton,
  InputAdornment,
  Avatar,
  Chip,
  Divider
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { astrologerRegistrationService } from '../../services/api';
import { loginUser } from '../../store/slices/authSlice';
import { useAuth } from '../../contexts/AuthContext';

const PasswordCreationForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setAuthUser } = useAuth();

  // Get token, email, and profile from location state
  const { token, email, profile } = location.state || {};

  const [formData, setFormData] = useState({
    firstName: profile?.firstName || '',
    lastName: profile?.lastName || '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!token || !email) {
      navigate('/unauthorized');
    }
  }, [token, email, navigate]);

  const validatePassword = (pwd) => {
    const hasMinLength = pwd.length >= 8;
    const hasUppercase = /[A-Z]/.test(pwd);
    const hasLowercase = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(pwd);

    let charTypeCount = 0;
    if (hasUppercase) charTypeCount++;
    if (hasLowercase) charTypeCount++;
    if (hasNumber) charTypeCount++;
    if (hasSpecial) charTypeCount++;

    let strength = 0;
    if (hasMinLength) strength += 30;
    if (hasLowercase) strength += 20;
    if (charTypeCount >= 3) strength += 50;
    else if (charTypeCount >= 2) strength += 30;

    return {
      isValid: hasMinLength && hasLowercase && charTypeCount >= 2,
      strength,
      errors: {
        minLength: !hasMinLength ? 'At least 8 characters' : null,
        lowercase: !hasLowercase ? 'At least one lowercase letter' : null,
        charTypes: charTypeCount < 2 ? 'Mix of uppercase, numbers, or symbols required' : null,
      }
    };
  };

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));

    if (field === 'password') {
      const validation = validatePassword(value);
      setPasswordStrength(validation.strength);
      setErrors(prev => ({ ...prev, password: value && !validation.isValid ? 'Password does not meet requirements' : null }));
    }
    if (field === 'confirmPassword') {
      setErrors(prev => ({ ...prev, confirmPassword: value && value !== formData.password ? 'Passwords do not match' : null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';

    const pwdValidation = validatePassword(formData.password);
    if (!pwdValidation.isValid) newErrors.password = 'Password does not meet requirements';
    if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const registrationData = {
        token,
        email,
        password: formData.password,
        confirmPassword: formData.password,
        firstName: formData.firstName.trim() || undefined,
        lastName: formData.lastName.trim() || undefined,
        phoneNumber: formData.phoneNumber.trim(),
      };

      const response = await astrologerRegistrationService.registerAstrologer(registrationData);

      if (response.data.token) {
        const { token: authToken, user } = response.data;
        const astrologerUser = { ...user, role: user.role || 'astrologer' };

        localStorage.setItem('token', authToken);
        localStorage.setItem('user', JSON.stringify(astrologerUser));

        dispatch(loginUser.fulfilled({ token: authToken, user: astrologerUser }, 'astrologer-register', null));
        setAuthUser(astrologerUser, authToken);
      }

      navigate('/astrologer/dashboard');
    } catch (error) {
      const msg = error.response?.data?.error || error.response?.data?.message || 'Failed to create account. Please try again.';
      setErrors(prev => ({ ...prev, submit: msg }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStrengthColor = () => {
    if (passwordStrength < 40) return 'error';
    if (passwordStrength < 70) return 'warning';
    return 'success';
  };

  if (!token || !email) return null;

  const fullName = [profile?.firstName, profile?.lastName].filter(Boolean).join(' ');

  return (
    <Container maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Create Your Account
          </Typography>

          {/* Profile Preview (read-only, set by admin) */}
          {fullName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar
                src={profile?.profileImageUrl || ''}
                sx={{ width: 56, height: 56, bgcolor: '#1976d2' }}
              >
                {profile?.firstName?.[0]?.toUpperCase() || '?'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold">{fullName}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile?.specialization} &bull; {profile?.experienceYears} yrs exp
                </Typography>
              </Box>
            </Box>
          )}

          {profile?.bio && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {profile.bio}
            </Typography>
          )}

          {(profile?.pricePerMinuteChat > 0 || profile?.pricePerMinuteCall > 0) && (
            <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
              {profile.pricePerMinuteChat > 0 && <Chip label={`Chat ₹${profile.pricePerMinuteChat}/min`} size="small" variant="outlined" />}
              {profile.pricePerMinuteCall > 0 && <Chip label={`Call ₹${profile.pricePerMinuteCall}/min`} size="small" variant="outlined" />}
              {profile.pricePerMinuteVideo > 0 && <Chip label={`Video ₹${profile.pricePerMinuteVideo}/min`} size="small" variant="outlined" />}
            </Box>
          )}

          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
            Joining as: <strong>{email}</strong>
          </Typography>

          <form onSubmit={handleSubmit}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={handleChange('firstName')}
                error={!!errors.firstName}
                helperText={errors.firstName}
                margin="normal"
              />
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange('lastName')}
                error={!!errors.lastName}
                helperText={errors.lastName}
                margin="normal"
              />
            </Box>

            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={handleChange('phoneNumber')}
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber}
              margin="normal"
              required
              inputProps={{ maxLength: 15 }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange('password')}
              error={!!errors.password}
              helperText={errors.password}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {formData.password && (
              <Box sx={{ mt: 0.5, mb: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={passwordStrength}
                  color={getStrengthColor()}
                  sx={{ mb: 0.5 }}
                />
                {Object.values(validatePassword(formData.password).errors).map((err, i) =>
                  err ? (
                    <Typography key={i} variant="caption" color="error" display="block">
                      &bull; {err}
                    </Typography>
                  ) : null
                )}
              </Box>
            )}

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {errors.submit && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {errors.submit}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isSubmitting || passwordStrength < 50}
              sx={{ mt: 3, mb: 2 }}
            >
              {isSubmitting ? 'Creating Account...' : 'Create Account & Continue'}
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default PasswordCreationForm;
